// אבני בניין קטנות (11, 2026-10-13): כרטיסי KPI עם קו מגמה, בחירה עם חיפוש (combobox), בורר מקטעים, קו מגמה.
// DOM בלבד, בלי globals (fmt מגיע כארגומנט) — רץ ב-iframe של בלוק app (ab.kpis / ab.select / ab.segmented / ab.spark).
// אותן מחלקות CSS כמו KpiBlock של הדף המארח (kit.css). ה-import מוסר בהזרקה ל-iframe.
import { normalize, sparkPath } from './series.js';

const SVG_NS = 'http://www.w3.org/2000/svg';
let seq = 0;
const uid = (p) => `${p}-${++seq}`;
const mk = (doc, tag, cls, text) => {
  const e = doc.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** קו מגמה כ-SVG (רוחב 100%, גובה h). */
export function sparkSvg(doc, values, o) {
  o = o || {};
  const w = 100;
  const hh = o.height || 28;
  const svg = doc.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', `ab-spark${o.className ? ` ${o.className}` : ''}`);
  svg.setAttribute('viewBox', `0 0 ${w} ${hh}`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const p = doc.createElementNS(SVG_NS, 'path');
  p.setAttribute('d', sparkPath(values, w, hh, 3));
  p.setAttribute('fill', 'none');
  p.setAttribute('stroke', o.color || 'currentColor');
  p.setAttribute('stroke-width', '2');
  p.setAttribute('stroke-linecap', 'round');
  p.setAttribute('stroke-linejoin', 'round');
  p.setAttribute('vector-effect', 'non-scaling-stroke');
  svg.appendChild(p);
  return svg;
}

/** טון של שינוי: good/bad/flat לפי הכיוון הטוב (good: 'down' = ירידה טובה — הוצאות, חובות). */
export function deltaTone(d, good) {
  if (d === null || d === undefined || d === 0) return 'flat';
  return (d > 0) === (good !== 'down') ? 'good' : 'bad';
}

/** כרטיסי KPI: [{labelHe, value, type, delta?, hintHe?, spark?, good?}] — אותו מראה כמו בלוק kpi. */
export function renderKpis(el, cards, deps) {
  const doc = el.ownerDocument;
  const pct = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 1, signDisplay: 'exceptZero' });
  const wrap = mk(doc, 'div', 'ab-kpis');
  for (const c of cards || []) {
    const card = mk(doc, 'div', 'ab-kpi');
    card.dataset.testid = 'kpi-card';
    card.appendChild(mk(doc, 'div', 'ab-kpi-label', c.labelHe ?? ''));
    const val = mk(doc, 'div', 'ab-kpi-value');
    const b = mk(doc, 'bdi', '', deps.fmt(c.value, c.type || 'number'));
    b.dir = 'auto';
    val.appendChild(b);
    card.appendChild(val);
    const d = typeof c.delta === 'number' ? c.delta : null;
    const tone = deltaTone(d, c.good);
    if (d !== null || (c.spark && c.spark.length)) {
      const foot = mk(doc, 'div', 'ab-kpi-foot');
      if (d !== null) {
        const pill = mk(doc, 'div', `ab-kpi-delta ${tone}`);
        const ar = mk(doc, 'span', '', d > 0 ? '▲' : d < 0 ? '▼' : '•');
        ar.setAttribute('aria-hidden', 'true');
        const v = mk(doc, 'bdi', '', `${pct.format(d * 100)}%`);
        v.dir = 'ltr';
        pill.append(ar, ' ', v);
        foot.appendChild(pill);
      }
      if (c.spark && c.spark.length) foot.appendChild(sparkSvg(doc, c.spark, { className: `ab-kpi-spark ${tone}` }));
      card.appendChild(foot);
    }
    if (c.hintHe) card.appendChild(mk(doc, 'div', 'ab-kpi-hint', c.hintHe));
    wrap.appendChild(card);
  }
  el.appendChild(wrap);
  return { el: wrap, destroy: () => wrap.remove() };
}

const optList = (options) => (options || []).map((o) => (o !== null && typeof o === 'object' ? { value: o.value, labelHe: String(o.labelHe ?? o.value) } : { value: o, labelHe: String(o) }));

/**
 * בחירה עם חיפוש (ARIA combobox): {options: (string | {value, labelHe})[], value?, multi?, labelHe?, placeholderHe?, onChange?(value)}.
 * מקלדת: חצים, Enter, Escape, Backspace (מסיר את השבב האחרון ב-multi). קלט 16px (בלי זום ב-iOS).
 * → {el, value(), set(v), setOptions(options), destroy()}
 */
export function createSelect(el, o) {
  const doc = el.ownerDocument;
  let options = optList(o.options);
  const multi = !!o.multi;
  let value = multi ? (Array.isArray(o.value) ? o.value.slice() : []) : (o.value ?? null);
  let open = false;
  let active = -1;
  let filtered = options;
  const id = uid('ab-sel');
  const root = mk(doc, 'div', `ab-sel${multi ? ' ab-sel-multi' : ''}`);
  if (o.labelHe) {
    const lab = mk(doc, 'label', 'ab-sel-label', o.labelHe);
    lab.htmlFor = `${id}-in`;
    root.appendChild(lab);
  }
  const box = mk(doc, 'div', 'ab-sel-box');
  const chips = mk(doc, 'span', 'ab-sel-chips');
  const input = mk(doc, 'input', 'ab-sel-input');
  input.id = `${id}-in`;
  input.type = 'text';
  input.autocomplete = 'off';
  input.placeholder = o.placeholderHe || 'בחר או הקלד לחיפוש…';
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', `${id}-list`);
  const list = mk(doc, 'ul', 'ab-sel-list');
  list.id = `${id}-list`;
  list.setAttribute('role', 'listbox');
  if (multi) list.setAttribute('aria-multiselectable', 'true');
  list.hidden = true;
  box.append(chips, input);
  root.append(box, list);
  el.appendChild(root);

  const labelOf = (v) => options.find((x) => x.value === v)?.labelHe ?? String(v ?? '');
  const isSel = (v) => (multi ? value.includes(v) : value === v);
  const emit = () => o.onChange && o.onChange(multi ? value.slice() : value);
  const renderChips = () => {
    chips.textContent = '';
    if (!multi) {
      if (!open) input.value = value === null ? '' : labelOf(value);
      return;
    }
    for (const v of value) {
      const c = mk(doc, 'span', 'ab-chip');
      c.appendChild(mk(doc, 'bdi', '', labelOf(v)));
      const x = mk(doc, 'button', 'ab-chip-x', '×');
      x.type = 'button';
      x.setAttribute('aria-label', `הסר ${labelOf(v)}`);
      x.addEventListener('click', (e) => {
        e.stopPropagation();
        value = value.filter((y) => y !== v);
        renderChips();
        renderList();
        emit();
      });
      c.appendChild(x);
      chips.appendChild(c);
    }
  };
  const renderList = () => {
    list.textContent = '';
    const q = open ? normalize(input.value) : '';
    filtered = options.filter((x) => !q || normalize(x.labelHe).includes(q) || normalize(String(x.value)).includes(q)).slice(0, 300);
    if (active >= filtered.length) active = filtered.length - 1;
    filtered.forEach((x, i) => {
      const li = mk(doc, 'li', 'ab-sel-opt');
      li.id = `${id}-o${i}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', String(isSel(x.value)));
      if (i === active) li.classList.add('ab-active');
      li.appendChild(mk(doc, 'bdi', '', x.labelHe));
      li.addEventListener('mousedown', (e) => e.preventDefault());
      li.addEventListener('click', () => pick(i));
      list.appendChild(li);
    });
    if (!filtered.length) list.appendChild(mk(doc, 'li', 'ab-sel-empty', 'אין תוצאות'));
    if (active >= 0) {
      input.setAttribute('aria-activedescendant', `${id}-o${active}`);
      list.children[active]?.scrollIntoView?.({ block: 'nearest' });
    } else input.removeAttribute('aria-activedescendant');
  };
  const setOpen = (v) => {
    open = v;
    list.hidden = !v;
    input.setAttribute('aria-expanded', String(v));
    root.classList.toggle('ab-open', v);
    if (!v) {
      active = -1;
      if (multi) input.value = '';
      renderChips();
    }
    renderList();
  };
  const pick = (i) => {
    const x = filtered[i];
    if (!x) return;
    if (multi) {
      value = isSel(x.value) ? value.filter((y) => y !== x.value) : value.concat([x.value]);
      input.value = '';
      active = Math.min(i, filtered.length - 1);
      renderChips();
      renderList();
    } else {
      value = x.value;
      setOpen(false);
    }
    emit();
  };
  input.addEventListener('focus', () => {
    if (!multi) input.select();
    setOpen(true);
  });
  input.addEventListener('blur', () => setOpen(false));
  input.addEventListener('input', () => {
    active = 0;
    if (!open) setOpen(true);
    else renderList();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setOpen(true);
      const n = filtered.length;
      if (!n) return;
      active = e.key === 'ArrowDown' ? (active + 1) % n : (active - 1 + n) % n;
      renderList();
    } else if (e.key === 'Enter') {
      if (open && active >= 0) {
        e.preventDefault();
        pick(active);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    } else if (e.key === 'Backspace' && multi && !input.value && value.length) {
      value = value.slice(0, -1);
      renderChips();
      renderList();
      emit();
    }
  });
  box.addEventListener('mousedown', (e) => {
    if (e.target === box || e.target === chips) {
      e.preventDefault();
      input.focus();
    }
  });
  renderChips();
  return {
    el: root,
    value: () => (multi ? value.slice() : value),
    set(v) {
      value = multi ? (Array.isArray(v) ? v.slice() : []) : (v ?? null);
      renderChips();
      renderList();
    },
    setOptions(opts) {
      options = optList(opts);
      renderChips();
      renderList();
    },
    destroy: () => root.remove(),
  };
}

/** בורר מקטעים (כמו טבלה/גרף): {options: (string | {value, labelHe})[], value?, labelHe?, onChange?(value)} → {el, value(), set(v), destroy()} */
export function createSegmented(el, o) {
  const doc = el.ownerDocument;
  const options = optList(o.options);
  let value = o.value ?? options[0]?.value ?? null;
  const root = mk(doc, 'div', 'ab-seg');
  root.setAttribute('role', 'group');
  if (o.labelHe) root.setAttribute('aria-label', o.labelHe);
  const btns = options.map((x) => {
    const b = mk(doc, 'button', '', x.labelHe);
    b.type = 'button';
    b.addEventListener('click', () => {
      if (value === x.value) return;
      value = x.value;
      sync();
      if (o.onChange) o.onChange(value);
    });
    root.appendChild(b);
    return b;
  });
  const sync = () => btns.forEach((b, i) => b.setAttribute('aria-pressed', String(options[i].value === value)));
  sync();
  el.appendChild(root);
  return {
    el: root,
    value: () => value,
    set(v) {
      value = v;
      sync();
    },
    destroy: () => root.remove(),
  };
}
